import {useState, useEffect} from 'react';
import {PublicClientApplication, InteractionRequiredAuthError} from '@azure/msal-browser';

const MSAL_CACHE_LOCATION = "sessionStorage";

const msalConfigEntra = {
    auth: {
        clientId: process.env.REACT_APP_ENTRA_AUTH_CLIENT_ID,
        authority: process.env.REACT_APP_ENTRA_AUTHORITY,
        redirectUri: process.env.REACT_APP_CONTENT_AUTH_REDIRECT_URI || window.location.origin,
    },
    cache: {
        cacheLocation: MSAL_CACHE_LOCATION,
        storeAuthStateInCookie: false,
    },
};

export const getMsalInstance = () => {
    if (!window.msalEntraInstance) {
        try {
            window.msalEntraInstance = new PublicClientApplication(msalConfigEntra);
            window.msalInitialized = false;
        } catch (err) {
            console.error("Failed to create MSAL instance:", err);
        }
    }
    return window.msalEntraInstance;
};

const GRAPH_SCOPES = [
    "https://graph.microsoft.com/User.Read",
    "Files.Read.All",
    "https://graph.microsoft.com/Sites.Read.All",
];

export const acquireSharePointToken = async (resource) => {
    const instance = getMsalInstance();

    if (!window.msalInitialized) {
        await instance.initialize();
        window.msalInitialized = true;
    }

    const scopes = [`${resource}/.default`];
    let accounts = instance.getAllAccounts();

    if (accounts.length === 0) {
        const loginResult = await instance.loginPopup({scopes});
        instance.setActiveAccount(loginResult.account);
        accounts = instance.getAllAccounts();
    }

    try {
        const result = await instance.acquireTokenSilent({account: accounts[0], scopes});
        return result.accessToken;
    } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
            const result = await instance.loginPopup({scopes});
            instance.setActiveAccount(result.account);
            const tokenResult = await instance.acquireTokenSilent({account: result.account, scopes});
            return tokenResult.accessToken;
        }
        throw silentError;
    }
};

export default function useEntraAuthentication() {
    const [initialized, setInitialized] = useState(false);
    const [account, setAccount] = useState(null);
    const [interactionInProgress, setInteractionInProgress] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {

        const initializeEntra = async () => {

            try {

                const instance = getMsalInstance();

                if (!instance) {
                    setError("MSAL instance not available. Check Entra ID configuration.");
                    setInitialized(true);
                    return;
                }

                if (!process.env.REACT_APP_ENTRA_AUTH_CLIENT_ID) {
                    setError("Entra ID configuration missing. Please set REACT_APP_ENTRA_AUTH_CLIENT_ID environment variable.");
                    setInitialized(true);
                    return;
                }

                if (!window.msalInitialized) {
                    await instance.initialize();
                    window.msalInitialized = true;
                }

                const redirectResult = await instance.handleRedirectPromise();

                if (redirectResult && redirectResult.account) {
                    setAccount(redirectResult.account);
                } else {
                    const accounts = instance.getAllAccounts();

                    if (accounts.length > 0) {
                        setAccount(accounts[0]);
                    } else {
                        setInteractionInProgress(true);
                        await instance.loginRedirect({ scopes: GRAPH_SCOPES });
                        return;
                    }
                }

                setInitialized(true);
            } catch (err) {
                setError(`Failed to initialize Entra authentication: ${err.message}`);
                setInitialized(true);
            }
        };
        initializeEntra();
    }, []);

    const signIn = async () => {

        const instance = getMsalInstance();

        if (!initialized) {
            setError("Entra not initialized");
            return;
        }

        if (!instance) {
            setError("MSAL instance not available");
            return;
        }

        if (interactionInProgress) return;

        setInteractionInProgress(true);
        setError(null);

        try {
            await instance.loginRedirect({ scopes: GRAPH_SCOPES });
        } catch (err) {
            setError(`Entra login failed: ${err.message}`);
            setInteractionInProgress(false);
        }
    };

    const acquireToken = async () => {

        const instance = getMsalInstance();

        if (!window.msalInitialized) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (!window.msalInitialized) {
                throw new Error("MSAL initialization timeout");
            }
        }

        if (!instance) throw new Error("MSAL instance not available");

        if (!account) {
            await signIn();
            return;
        }

        if (interactionInProgress) {

            let attempts = 0;
            while (interactionInProgress && attempts < 10) {
                await new Promise(resolve => setTimeout(resolve, 500));
                attempts++;
            }
            if (interactionInProgress) {
                console.log("Interaction timeout, resetting state");
                setInteractionInProgress(false);
            }
        }

        setInteractionInProgress(true);
        setError(null);
        try {
            const tokenRequest = {
                account: account,
                scopes: GRAPH_SCOPES,
            };
            try {

                const silentResult = await instance.acquireTokenSilent(tokenRequest);
                setInteractionInProgress(false);
                return silentResult.accessToken;
            } catch (silentError) {
                
                if (silentError instanceof InteractionRequiredAuthError) {
                    console.log("Triggering interactive token acquisition");
                    await instance.acquireTokenRedirect(tokenRequest);
                    return;
                }
                throw silentError;
            }
        } catch (error) {
            setInteractionInProgress(false);
            throw error;
        }
    };

    const logout = () => {
        const instance = getMsalInstance();
        if (!initialized || !instance) return;
        instance.logoutRedirect();
    };

    return {
        initialized,
        account,
        interactionInProgress,
        error,
        signIn,
        acquireToken,
        logout
    };
}